import { html, CSSResultGroup } from 'lit';
import { property } from 'lit/decorators.js';
import { ifDefined } from 'lit/directives/if-defined.js';
import { BlocksElement } from '../../BlocksElement';
import styles from '../icon.styles';
import type { IconColorType, IconSize } from './icon.model';

/**
 * Star component
 * @csspart base- The component's base wrapper
 */
export class BlocksIconStar extends BlocksElement {static styles: CSSResultGroup = styles;

  /**
   * Icon color variant
   * <cat-text-passage size="sm">
   * <ul>
   * <li>No color defined renders an icon with the currentColor of its parent element</li>
   * <li>**content-strong** renders an icon with the cat-theme-color-content-strong color token</li>
   * <li>**knockout** renders an icon with the cat-theme-color-content-knockout color token</li>
   * <li>**knockout-brand** renders a renders an icon with the cat-theme-color-content-knockout-brand color token</li>
   * <li>**information** renders an icon with the cat-theme-color-content-utility-information color token</li>
   * <li>**success** renders an icon with the cat-theme-color-content-utility-success color token</li>
   * <li>**warning** renders a renders an icon with the cat-theme-color-content-utility-warning color token</li>
   * <li>**error** renders an icon with the cat-theme-color-content-utility-error color token</li>
   * </ul>
   * </cat-text-passage>
   */
  @property({ type: String })
  color: IconColorType;

  /**
   * Title
   * 1) Icons should always have associated text. Use this to supply `aria-label` to icon if it stands by itself without any visual/visually-hidden text
   */
  @property({ type: String })
  iconTitle: string | undefined;

  /**
   * Size property
   * - Default size is 16px
   * - **sm** renders a larger size than default (12px)
   * - **lg** renders a larger size than default (24px)
   * - **xl** renders a larger size than the lg variant (32px);
   */
  @property({ type: String })
  size: IconSize;

  connectedCallback() {
    super.connectedCallback();
    this.classList.add('cat-icon');
  }

  render() {
    const componentClassName = this.componentClassNames('cat-c-icon', {
      'cat-c-icon--sm': this.size === 'sm',
      'cat-c-icon--lg': this.size === 'lg',
      'cat-c-icon--xl': this.size === 'xl',
      'cat-c-icon--content-strong': this.color === 'content-strong',
      'cat-c-icon--knockout': this.color === 'knockout',
      'cat-c-icon--knockout-brand': this.color === 'knockout-brand',
      'cat-c-icon--information': this.color === 'information',
      'cat-c-icon--success': this.color === 'success',
      'cat-c-icon--warning': this.color === 'warning',
      'cat-c-icon--error': this.color === 'error'
    });

    return html`
      <span part="base" class="${componentClassName}" role="presentation">
        <svg
          aria-hidden="${!this.iconTitle}"
          aria-label="${ifDefined(this.iconTitle)}"
          role="${this.iconTitle && 'img'}"
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            fill-rule="evenodd"
            clip-rule="evenodd"
            d="M12.0209 3.04956C12.0032 3.04956 11.986 3.0546 11.9712 3.06408C11.9563 3.07356 11.9445 3.08709 11.9371 3.10308L11.9286 3.12078L9.45435 8.116C9.34447 8.35581 9.17499 8.5636 8.96194 8.71949C8.74826 8.87584 8.49807 8.97462 8.23539 9.00648L2.77179 9.8159C2.75457 9.81845 2.73728 9.82041 2.71993 9.82175C2.70213 9.82313 2.68512 9.82965 2.67094 9.8405C2.65677 9.85136 2.64605 9.86609 2.64008 9.88291C2.63411 9.89974 2.63315 9.91793 2.63731 9.93529C2.64147 9.95265 2.65057 9.96844 2.66352 9.98073L2.66791 9.98489L6.65744 13.8342C6.84697 14.0129 6.98932 14.2357 7.07171 14.4829C7.15454 14.7314 7.17416 14.9966 7.12879 15.2546C7.12863 15.2555 7.12847 15.2564 7.1283 15.2574L6.17766 20.8211L6.17726 20.8234C6.17379 20.8433 6.17598 20.8638 6.18358 20.8825C6.19117 20.9012 6.20387 20.9174 6.22023 20.9293C6.23658 20.9411 6.25593 20.9482 6.27608 20.9496C6.29623 20.951 6.31637 20.9467 6.33421 20.9372L6.3358 20.9363L11.2649 18.3314C11.2715 18.3279 11.2781 18.3246 11.2848 18.3213C11.5139 18.2088 11.7657 18.1503 12.0209 18.1503C12.276 18.1503 12.5278 18.2088 12.7569 18.3213C12.7636 18.3246 12.7703 18.3279 12.7768 18.3314L17.7075 20.9372C17.7253 20.9467 17.7455 20.951 17.7656 20.9496C17.7858 20.9482 17.8051 20.9411 17.8215 20.9293C17.8378 20.9174 17.8505 20.9012 17.8581 20.8825C17.8657 20.8638 17.8679 20.8433 17.8645 20.8234L16.9134 15.3197C16.9133 15.3191 16.9132 15.3184 16.913 15.3177C16.8675 15.0595 16.8871 14.794 16.97 14.5453C17.0518 14.3 17.1926 14.0787 17.3799 13.9007L21.3196 9.99221C21.3248 9.98702 21.3301 9.9819 21.3355 9.97686C21.3483 9.96489 21.3574 9.94954 21.3618 9.93258C21.3662 9.91562 21.3657 9.89777 21.3603 9.8811C21.3549 9.86443 21.3449 9.84963 21.3314 9.83844C21.318 9.82725 21.3016 9.82012 21.2842 9.81788L21.2699 9.8159L15.8063 9.00648C15.5436 8.97462 15.2935 8.87584 15.0798 8.71949C14.8667 8.5636 14.6972 8.35581 14.5874 8.116L12.1131 3.12078L12.7852 2.78789L12.1046 3.10308C12.0972 3.08709 12.0854 3.07356 12.0706 3.06408C12.0557 3.0546 12.0385 3.04956 12.0209 3.04956ZM13.4609 2.46241C13.333 2.19121 13.131 1.96158 12.8782 1.80005C12.6221 1.63648 12.3247 1.54956 12.0209 1.54956C11.717 1.54956 11.4196 1.63648 11.1636 1.80005C10.9107 1.96158 10.7088 2.19121 10.5808 2.46241L8.10434 7.46205C8.09966 7.47149 8.09518 7.48104 8.0909 7.49068C8.08767 7.49795 8.0826 7.50425 8.07618 7.50894C8.06976 7.51364 8.06222 7.51657 8.05431 7.51745C8.04518 7.51846 8.03606 7.51964 8.02697 7.52099L2.57371 8.32888C2.27763 8.35753 1.99527 8.46857 1.75881 8.6497C1.51434 8.83696 1.32942 9.09108 1.22644 9.3813C1.12346 9.67152 1.10684 9.98537 1.1786 10.2848C1.25009 10.5832 1.40625 10.8545 1.62826 11.0662L5.61954 14.9172L5.62828 14.9255C5.63763 14.9342 5.64464 14.9451 5.64868 14.9573C5.65273 14.9694 5.65368 14.9823 5.65144 14.9949L5.65058 14.9998L4.69949 20.5661C4.69941 20.5665 4.69933 20.567 4.69925 20.5675C4.64796 20.8636 4.68067 21.1681 4.79369 21.4466C4.90688 21.7255 5.09609 21.967 5.33977 22.1437C5.58346 22.3204 5.87185 22.4251 6.17212 22.4459C6.47194 22.4668 6.77159 22.4032 7.03708 22.2623C7.03747 22.2621 7.03787 22.2619 7.03826 22.2617L11.9519 19.6649C11.9736 19.6553 11.9971 19.6503 12.0209 19.6503C12.0446 19.6503 12.0681 19.6553 12.0898 19.6649L17.0034 22.2617C17.0039 22.2619 17.0043 22.2622 17.0048 22.2624C17.2702 22.4032 17.5698 22.4668 17.8696 22.4459C18.1699 22.4251 18.4583 22.3204 18.7019 22.1437C18.9456 21.967 19.1348 21.7255 19.248 21.4466C19.3612 21.1678 19.3938 20.8629 19.3423 20.5665C19.3423 20.5667 19.3423 20.5664 19.3423 20.5665L18.3909 15.0608L18.3903 15.0573C18.388 15.0447 18.389 15.0318 18.393 15.0196C18.3971 15.0075 18.4041 14.9966 18.4134 14.9878C18.4189 14.9827 18.4243 14.9775 18.4296 14.9722L22.3694 11.0636C22.5852 10.8585 22.7392 10.597 22.8139 10.3087C22.8897 10.0162 22.8806 9.70815 22.7879 9.42059C22.6951 9.13304 22.5225 8.87781 22.2901 8.68472C22.0598 8.49341 21.7803 8.37091 21.4837 8.33121L16.0147 7.52099C16.0057 7.51964 15.9965 7.51846 15.9874 7.51745C15.9795 7.51657 15.972 7.51364 15.9655 7.50894C15.9591 7.50424 15.954 7.49795 15.9508 7.49068C15.9465 7.48104 15.9421 7.47149 15.9374 7.46205L13.4609 2.46241Z"
          />
        </svg>
      </span>
    `;
  }
}

if (customElements.get('cat-icon-star') === undefined) {
  customElements.define('cat-icon-star', BlocksIconStar);
}

declare global {
  interface HTMLElementTagNameMap {
    'cat-icon-star': BlocksIconStar;
  }
}
